import React, { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { Download, FileSpreadsheet, X, Wallet, Send, BadgeCheck, Hourglass, XCircle, AlertTriangle } from 'lucide-react';
import { Button, Select, SearchInput, Segmented, Skeleton, ErrorBlock, useLoad, downloadFile, errMsg, money } from '../../kit';

// Pieces shared by the Claims tabs:
//   MoneySummary   the money at a glance: ready, claimed, paid, owed (with overdue), rejected, each
//                  split by NDIA / plan-managed / self-managed, and what is owed by age.
//   useClaimFilters + FilterBar   filters kept in the address (so a link or Back keeps them), sent
//                  to the server as one params object.
//   ExportButtons  the filtered list as CSV or Excel.

export const FUNDING_META = {
  ndia: { label: 'NDIA', long: 'NDIA-managed', color: '#7c3aed' },
  plan: { label: 'Plan', long: 'Plan-managed', color: '#0891b2' },
  self: { label: 'Self', long: 'Self-managed', color: '#64748b' },
};
const FUNDS = ['ndia', 'plan', 'self'];

// ── Money summary ─────────────────────────────────────────────────────────────────────────────
const pad = (n) => String(n).padStart(2, '0');
const isoOf = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
function periodOf(key) {
  const t = new Date();
  if (key === 'last_month') return { from: isoOf(new Date(t.getFullYear(), t.getMonth() - 1, 1)), to: isoOf(new Date(t.getFullYear(), t.getMonth(), 0)) };
  if (key === 'fy') { const y = t.getMonth() >= 6 ? t.getFullYear() : t.getFullYear() - 1; return { from: `${y}-07-01`, to: isoOf(t) }; }
  if (key === 'last_fy') { const y = t.getMonth() >= 6 ? t.getFullYear() - 1 : t.getFullYear() - 2; return { from: `${y}-07-01`, to: `${y + 1}-06-30` }; }
  return { from: isoOf(new Date(t.getFullYear(), t.getMonth(), 1)), to: isoOf(t) };
}
const PERIODS = [{ value: 'month', label: 'This month' }, { value: 'last_month', label: 'Last month' }, { value: 'fy', label: 'This financial year' }, { value: 'last_fy', label: 'Last financial year' }];
const AGE_TONE = { '0_30': '#10b981', '31_60': '#f59e0b', '61_90': '#f97316', '90_plus': '#e11d48' };

export function MoneySummary({ ready, readyLoading, signal, onGo }) {
  const [sp, setSp] = useSearchParams();
  const key = PERIODS.some((p) => p.value === sp.get('sum')) ? sp.get('sum') : 'month';
  const params = useMemo(() => ({ ...periodOf(key), _s: signal || 0 }), [key, signal]);
  const s = useLoad('/sc/claims/summary', params);
  const setKey = (v) => setSp((p) => { const n = new URLSearchParams(p); if (v === 'month') n.delete('sum'); else n.set('sum', v); return n; }, { replace: true });
  const d = s.data;
  const periodWord = (PERIODS.find((p) => p.value === key) || PERIODS[0]).label.toLowerCase();

  return (
    <section aria-labelledby="sc-money" className="mb-5 rounded-2xl border border-slate-200/80 bg-white shadow-card">
      <div className="flex flex-wrap items-center justify-between gap-2 px-5 pt-4 pb-3">
        <h2 id="sc-money" className="text-[15px] font-semibold text-slate-900">Money at a glance</h2>
        <div className="max-w-full overflow-x-auto"><Segmented size="sm" value={key} onChange={setKey} options={PERIODS} /></div>
      </div>
      {s.error ? <div className="px-5 pb-4"><ErrorBlock error={s.error} onRetry={s.reload} /></div> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-slate-100 border-y border-slate-100">
            <Tile icon={Wallet} tone="#059669" label="Ready to claim" value={ready} loading={readyLoading} hint="Unclaimed time that passes the checks" onClick={() => onGo('ready')} />
            <Tile icon={Send} tone="#7c3aed" label={`Claimed ${periodWord}`} split={d && d.claimed} loading={!d} onClick={() => onGo('batches')} />
            <Tile icon={BadgeCheck} tone="#059669" label={`Paid ${periodWord}`} split={d && d.paid} loading={!d} onClick={() => onGo('payments')} />
            <Tile icon={Hourglass} tone="#4f46e5" label="Owed to you now" split={d && d.outstanding} loading={!d} onClick={() => onGo('invoices')}
              extra={d && d.overdue.total > 0 ? <span className="text-rose-700 font-semibold">{money(d.overdue.total)} overdue</span> : null} />
            <Tile wide icon={XCircle} tone="#e11d48" label="Rejected" split={d && d.rejected} loading={!d} onClick={() => onGo('rejected')}
              extra={d ? <span>{d.rejected.lines} {d.rejected.lines === 1 ? 'line' : 'lines'} to re-claim or write off</span> : null} />
          </div>
          <Aging data={d} onGo={onGo} />
        </>
      )}
    </section>
  );
}

function Tile({ icon: Icon, tone, label, value, split, loading, hint, extra, onClick, wide }) {
  const total = split ? split.total : value;
  return (
    <button type="button" onClick={onClick}
      className={clsx(wide && 'col-span-2 lg:col-span-1', 'group bg-white text-left px-5 py-4 hover:bg-slate-50/80 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500 min-w-0')}>
      <div className="flex items-center gap-2 text-[12.5px] font-semibold text-slate-600">
        <span className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${tone}14`, color: tone }}><Icon size={13} aria-hidden /></span>
        <span className="truncate">{label}</span>
      </div>
      {loading ? <Skeleton className="h-7 w-28 mt-2 rounded-lg" /> : (
        <p className="mt-2 text-[24px] leading-none font-semibold tabular-nums tracking-[-0.02em] text-slate-900">{money(total || 0)}</p>
      )}
      {split && !loading && <FundingSplit split={split} />}
      {!split && hint && <p className="mt-2 text-[12px] text-slate-500 leading-snug">{hint}</p>}
      {extra && <p className="mt-1.5 text-[12px] text-slate-600 leading-snug">{extra}</p>}
    </button>
  );
}

// A thin bar in the three funding colours, and the three amounts under it.
function FundingSplit({ split }) {
  const total = Number(split.total) || 0;
  return (
    <div className="mt-2.5">
      <div className="flex h-1.5 rounded-full overflow-hidden bg-slate-100" aria-hidden>
        {total > 0 && FUNDS.map((f) => Number(split[f]) > 0 && <span key={f} style={{ width: `${(split[f] / total) * 100}%`, background: FUNDING_META[f].color }} />)}
      </div>
      <dl className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11.5px] text-slate-500">
        {FUNDS.map((f) => (
          <div key={f} className="flex items-center gap-1">
            <dt className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full" style={{ background: FUNDING_META[f].color }} aria-hidden />{FUNDING_META[f].label}</dt>
            <dd className="tabular-nums text-slate-700">{money(split[f] || 0)}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

// What is owed, by how long it has been owed: NDIA lines since the batch went in, invoices since issue.
function Aging({ data, onGo }) {
  if (!data) return <div className="px-5 py-4"><Skeleton className="h-10 rounded-xl" /></div>;
  const total = Number(data.aging.totals.total) || 0;
  return (
    <div className="px-5 py-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
        <h3 className="text-[13px] font-semibold text-slate-800">Owed by age</h3>
        <p className="text-[12px] text-slate-500">NDIA lines from when the batch went in; invoices from their issue date.</p>
      </div>
      {total <= 0 ? <p className="text-[13px] text-slate-600">Nothing is owed right now.</p> : (
        <>
          <div className="flex h-3 rounded-full overflow-hidden bg-slate-100" role="img"
            aria-label={data.aging.buckets.map((b) => `${b.label}: ${money(b.total)}`).join(', ')}>
            {data.aging.buckets.map((b) => b.total > 0 && <span key={b.key} style={{ width: `${(b.total / total) * 100}%`, background: AGE_TONE[b.key] }} />)}
          </div>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {data.aging.buckets.map((b) => (
              <button type="button" key={b.key} onClick={() => onGo(b.key === '90_plus' || b.key === '61_90' ? 'overdue' : 'invoices')}
                className="text-left rounded-xl px-3 py-2 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">
                <p className="flex items-center gap-1.5 text-[12px] font-semibold text-slate-600">
                  <span className="w-2 h-2 rounded-full" style={{ background: AGE_TONE[b.key] }} aria-hidden />{b.label}
                  {b.key === '90_plus' && b.total > 0 && <AlertTriangle size={12} className="text-rose-600" aria-label="Needs follow-up" />}
                </p>
                <p className="mt-0.5 text-[16px] font-semibold tabular-nums text-slate-900">{money(b.total)}</p>
                <p className="text-[11.5px] text-slate-500 tabular-nums truncate">NDIA {money(b.ndia)} · Plan {money(b.plan)} · Self {money(b.self)}</p>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Filters in the address ────────────────────────────────────────────────────────────────────
// prefix keeps each tab's filters apart (l_ lines, i_ invoices, p_ payments).
const KEYS = ['q', 'st', 'fund', 'coord', 'client', 'batch', 'from', 'to', 'sort', 'dir', 'kind', 'voided', 'view'];
export function useClaimFilters(prefix, defaults = {}) {
  const [sp, setSp] = useSearchParams();
  // defaults is a literal at the call site; its values (not its identity) are what matter.
  const dkey = JSON.stringify(defaults);
  const f = useMemo(() => {
    const d = JSON.parse(dkey);
    const o = {};
    for (const k of KEYS) { const v = sp.get(`${prefix}${k}`); o[k] = v == null ? (d[k] ?? '') : v; }
    return o;
  }, [sp, prefix, dkey]);
  const set = useCallback((patch) => setSp((p) => {
    const d = JSON.parse(dkey);
    const n = new URLSearchParams(p);
    for (const [k, v] of Object.entries(patch)) {
      if (v == null || v === (d[k] ?? '')) n.delete(`${prefix}${k}`); else n.set(`${prefix}${k}`, v);
    }
    return n;
  }, { replace: true }), [setSp, prefix, dkey]);
  const clear = useCallback(() => {
    const d = JSON.parse(dkey);
    set(Object.fromEntries(KEYS.filter((k) => !['sort', 'dir', 'view'].includes(k)).map((k) => [k, d[k] ?? ''])));
  }, [set, dkey]);
  const active = KEYS.filter((k) => !['sort', 'dir', 'st', 'kind', 'view', 'voided'].includes(k)).some((k) => f[k] && f[k] !== (defaults[k] ?? ''));
  return { f, set, clear, active };
}
// Sort state for a DataTable that sorts on the server.
export const sortingOf = (f) => (f.sort ? [{ id: f.sort, desc: f.dir === 'desc' }] : []);
export const onSortOf = (set) => (s) => { const x = s && s[0]; set({ sort: x ? x.id : '', dir: x ? (x.desc ? 'desc' : 'asc') : '' }); };

const csv = (v) => String(v || '').split(',').filter(Boolean);
const toggle = (list, v) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

// FilterBar: search and pickers on the first row; status and funding chips and dates on the second.
// statuses: [{ value, label, count, alert }] (multi-select; none ticked = all).
export function FilterBar({ f, set, clear, active, searchPlaceholder, statuses, funding = true, participants, coordinators, batches, dateLabel = 'Dates', kinds }) {
  const st = csv(f.st);
  const fund = csv(f.fund);
  const [open, setOpen] = useState(false);
  const pickers = (
    <>
      {participants && (
        <div className="w-full sm:w-56"><Select value={f.client} onChange={(v) => set({ client: v })} allowEmpty emptyLabel="All participants" placeholder="All participants" options={participants} /></div>
      )}
      {coordinators && (
        <div className="w-full sm:w-52"><Select value={f.coord} onChange={(v) => set({ coord: v })} allowEmpty emptyLabel="All coordinators" placeholder="All coordinators" options={coordinators} /></div>
      )}
      {batches && (
        <div className="w-full sm:w-56"><Select value={f.batch} onChange={(v) => set({ batch: v })} allowEmpty emptyLabel="All batches" placeholder="All batches" options={batches} /></div>
      )}
    </>
  );
  return (
    <div className="space-y-2.5 mb-3">
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2">
        <SearchInput value={f.q} onChange={(v) => set({ q: v })} placeholder={searchPlaceholder} className="w-full sm:w-72" />
        <div className="hidden sm:contents">{pickers}</div>
        <button type="button" onClick={() => setOpen((v) => !v)} className="sm:hidden text-left text-[13px] font-semibold text-indigo-600" aria-expanded={open}>
          {open ? 'Fewer filters' : 'More filters'}
        </button>
        {open && <div className="sm:hidden flex flex-col gap-2">{pickers}</div>}
        {active && (
          <button type="button" onClick={clear} className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-slate-500 hover:text-slate-900 sm:ml-1">
            <X size={13} aria-hidden /> Clear filters
          </button>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {kinds && kinds.map((k) => <Chip key={k.value} on={f.kind === k.value} onClick={() => set({ kind: f.kind === k.value ? '' : k.value })}>{k.label}</Chip>)}
        {statuses && statuses.map((s) => (
          <Chip key={s.value} on={st.includes(s.value)} alert={s.alert} onClick={() => set({ st: toggle(st, s.value).join(',') })}>
            {s.label}{s.count > 0 && <span className={clsx('ml-1 tabular-nums', s.alert ? 'text-rose-700' : 'opacity-70')}>{s.count}</span>}
          </Chip>
        ))}
        {(statuses || kinds) && funding && <span className="w-px h-5 bg-slate-200 mx-1" aria-hidden />}
        {funding && FUNDS.map((x) => (
          <Chip key={x} on={fund.includes(x)} dot={FUNDING_META[x].color} onClick={() => set({ fund: toggle(fund, x).join(',') })}>{FUNDING_META[x].long}</Chip>
        ))}
        <span className="w-px h-5 bg-slate-200 mx-1" aria-hidden />
        <label className="inline-flex items-center gap-1.5 text-[12.5px] text-slate-600">
          <span className="font-semibold">{dateLabel}</span>
          <input type="date" value={f.from} max={f.to || undefined} onChange={(e) => set({ from: e.target.value })} aria-label={`${dateLabel} from`}
            className="h-8 rounded-lg bg-slate-50 ring-1 ring-inset ring-slate-200 px-2 text-[12.5px] focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          <span aria-hidden>to</span>
          <input type="date" value={f.to} min={f.from || undefined} onChange={(e) => set({ to: e.target.value })} aria-label={`${dateLabel} to`}
            className="h-8 rounded-lg bg-slate-50 ring-1 ring-inset ring-slate-200 px-2 text-[12.5px] focus:outline-none focus:ring-2 focus:ring-indigo-500" />
        </label>
      </div>
    </div>
  );
}
function Chip({ on, alert, dot, onClick, children }) {
  return (
    <button type="button" aria-pressed={on} onClick={onClick}
      className={clsx('inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[12.5px] font-semibold ring-1 ring-inset transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
        on ? 'bg-indigo-600 text-white ring-indigo-600' : alert ? 'bg-rose-50 text-rose-800 ring-rose-200 hover:bg-rose-100' : 'bg-white text-slate-700 ring-slate-200 hover:bg-slate-50')}>
      {dot && <span className="w-2 h-2 rounded-full" style={{ background: on ? '#fff' : dot }} aria-hidden />}
      {children}
    </button>
  );
}

// ── Exports ───────────────────────────────────────────────────────────────────────────────────
export function ExportButtons({ url, params, label = 'Export' }) {
  const [busy, setBusy] = useState(null);
  const go = async (format) => {
    setBusy(format);
    try { await downloadFile(url, { params: { ...params, format, limit: undefined, offset: undefined } }); }
    catch (e) { toast.error(errMsg(e, 'Could not export. Please try again.')); }
    finally { setBusy(null); }
  };
  return (
    <div className="inline-flex items-center gap-1" role="group" aria-label={label}>
      <Button size="sm" variant="secondary" icon={FileSpreadsheet} loading={busy === 'xlsx'} onClick={() => go('xlsx')}>Excel</Button>
      <Button size="sm" variant="secondary" icon={Download} loading={busy === 'csv'} onClick={() => go('csv')}>CSV</Button>
    </div>
  );
}

// API params from the filters (empty values left out).
export function apiParams(f, extra = {}) {
  const o = {
    q: f.q || undefined, status: f.st || undefined, funding: f.fund || undefined, coordinator_id: f.coord || undefined,
    client_id: f.client || undefined, batch_id: f.batch || undefined, date_from: f.from || undefined, date_to: f.to || undefined,
    sort: f.sort || undefined, dir: f.dir || undefined, kind: f.kind || undefined, voided: f.voided || undefined, ...extra,
  };
  Object.keys(o).forEach((k) => o[k] === undefined && delete o[k]);
  return o;
}
